import { SheetPickerList, type SheetPickerOption } from '@/components/global/SheetPickerList';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/shadcn/avatar';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/shadcn/radio-group';
import { useTranslations } from '@/hooks/useTranslations';
import { type User, type WorkOrderAssignment } from '@/types';
import { Check, Crown, Pencil, Trash2, UserPlus } from 'lucide-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

type AssignmentSelection = { userId: string; role: 'lead' | 'helper'; user?: User };

const getAssignmentUserId = (assignment: WorkOrderAssignment): string => {
    return assignment.user_id || assignment.user?.id || '';
};

// Helper function to map assignments - moved outside component to avoid dependency issues
const mapAssignments = (list: WorkOrderAssignment[]): AssignmentSelection[] =>
    list.map((a) => ({
        userId: getAssignmentUserId(a),
        role: a.role as 'lead' | 'helper',
        user: a.user,
    }));

const EMPTY_ASSIGNMENTS: WorkOrderAssignment[] = [];

function assignmentsEqual(a: AssignmentSelection[], b: AssignmentSelection[]): boolean {
    if (a === b) {
        return true;
    }
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (a[i].userId !== b[i].userId || a[i].role !== b[i].role) {
            return false;
        }
    }
    return true;
}

interface AssignWorkerSectionProps {
    assignments?: WorkOrderAssignment[];
    users: User[];
    onAssign: (assignments: Array<{ userId: string; role: 'lead' | 'helper' }>) => void;
    readOnly?: boolean;
    noTitle?: boolean;
    autoSave?: boolean;
    isEditing?: boolean;
    onEditingChange?: (isEditing: boolean) => void;
}

export interface AssignWorkerSectionRef {
    save: () => void;
}

export const AssignWorkerSection = forwardRef<AssignWorkerSectionRef, AssignWorkerSectionProps>(function AssignWorkerSection(
    {
        // IMPORTANT: use a stable default to avoid infinite update loops when used without `assignments` prop
        // (default param `[]` creates a new array every render, retriggering effects).
        assignments = EMPTY_ASSIGNMENTS,
        users,
        onAssign,
        readOnly = false,
        noTitle = false,
        autoSave = false,
        isEditing: externalIsEditing,
        onEditingChange,
    },
    ref,
) {
    const { t } = useTranslations();
    const [internalIsEditing, setInternalIsEditing] = useState(false);
    const [selectedAssignments, setSelectedAssignments] = useState<AssignmentSelection[]>(() => mapAssignments(assignments));
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [newUserId, setNewUserId] = useState('');

    // Use external editing state if provided, otherwise use internal state
    const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
    const setIsEditing = useCallback(
        (value: boolean) => {
            if (externalIsEditing !== undefined && onEditingChange) {
                onEditingChange(value);
            } else {
                setInternalIsEditing(value);
            }
        },
        [externalIsEditing, onEditingChange],
    );

    // Track previous editing state to detect when entering edit mode
    // Initialize to false so we can detect when entering edit mode even on first render
    const prevIsEditingRef = useRef(false);

    // Sync selectedAssignments when assignments prop changes (but only when not editing to avoid overwriting user changes)
    useEffect(() => {
        if (!isEditing) {
            const next = mapAssignments(assignments);
            setSelectedAssignments((prev) => (assignmentsEqual(prev, next) ? prev : next));
        }
    }, [assignments, isEditing]);

    // When entering edit mode, ensure selectedAssignments contains all existing assignments from props
    // This ensures existing assignments are preserved and shown when editing starts
    useEffect(() => {
        const isNowEditing = isEditing;

        if (isNowEditing && !prevIsEditingRef.current) {
            setSelectedAssignments(mapAssignments(assignments));
        }

        // Update the ref for next time
        prevIsEditingRef.current = isNowEditing;
    }, [isEditing, assignments]);

    const syncAssignments = (next: AssignmentSelection[]) => {
        setSelectedAssignments(next);
        if (autoSave) {
            // For autoSave mode, validate before saving
            const hasLeadInNext = next.some((a) => a.role === 'lead');
            const canSaveNext = hasLeadInNext || next.length === 0;

            if (!canSaveNext) {
                // Don't save if no lead is selected
                return;
            }

            // Filter out any assignments without userId
            const assignmentsToSave = next.filter((a) => a.userId && a.userId.trim() !== '').map((a) => ({ userId: a.userId, role: a.role }));

            // Validate: ensure exactly one lead (if there are assignments)
            if (assignmentsToSave.length > 0) {
                const leadCount = assignmentsToSave.filter((a) => a.role === 'lead').length;
                if (leadCount > 1) {
                    // If multiple leads, keep only the first one as lead
                    let foundFirstLead = false;
                    assignmentsToSave.forEach((a) => {
                        if (a.role === 'lead') {
                            if (foundFirstLead) {
                                a.role = 'helper';
                            } else {
                                foundFirstLead = true;
                            }
                        }
                    });
                }
            }

            onAssign(assignmentsToSave);
        }
    };

    const handleAddWorker = () => {
        if (!newUserId) {
            return;
        }

        // Check if worker is already assigned (check both selectedAssignments and saved assignments)
        const isAlreadyAssigned =
            selectedAssignments.some((a) => a.userId === newUserId) || assignments.some((a) => getAssignmentUserId(a) === newUserId);

        if (isAlreadyAssigned) {
            setNewUserId('');
            setIsAddDialogOpen(false);
            return;
        }

        const user = users.find((u) => u.id === newUserId);

        // Determine the role: if there's already a lead in selectedAssignments, default to helper
        // If no lead exists yet, first assignment becomes lead, subsequent ones become helper
        const hasExistingLead = selectedAssignments.some((a) => a.role === 'lead');
        const isFirstAssignment = selectedAssignments.length === 0;
        const newRole: 'lead' | 'helper' = isFirstAssignment || !hasExistingLead ? 'lead' : 'helper';

        // Explicitly append the new worker to existing assignments - preserve ALL existing workers
        const newAssignment: AssignmentSelection = { userId: newUserId, role: newRole, user };
        const next: AssignmentSelection[] = [...selectedAssignments, newAssignment];

        // Sync assignments (this will update state and save if autoSave is enabled)
        syncAssignments(next);

        setNewUserId('');
        setIsAddDialogOpen(false);
    };

    const handleRemoveWorker = (userId: string) => {
        // Allow removing any worker, including the lead
        // After removal, if no lead exists and there are still workers, require manual selection
        const next = selectedAssignments.filter((a) => a.userId !== userId) as AssignmentSelection[];

        syncAssignments(next);
    };

    const handleSetLead = (userId: string) => {
        const next = selectedAssignments.map((a) => ({
            ...a,
            role: a.userId === userId ? 'lead' : 'helper',
        })) as AssignmentSelection[];
        syncAssignments(next);
    };

    // Validation: check if there's a lead selected
    const hasLead = useMemo(() => {
        return selectedAssignments.some((a) => a.role === 'lead');
    }, [selectedAssignments]);

    // Can save if there's a lead OR no assignments at all (empty state is valid)
    const canSave = useMemo(() => {
        return hasLead || selectedAssignments.length === 0;
    }, [hasLead, selectedAssignments.length]);

    const handleSave = useCallback(() => {
        // Validate: must have a lead if there are any assignments
        if (!canSave) {
            toast.error(t('toasts.work_orders_assignworkersection.select_lead_before_saving', 'Please select a lead worker before saving'));
            return;
        }

        // Filter out any assignments without userId
        const assignmentsToSave = selectedAssignments
            .filter((a) => a.userId && a.userId.trim() !== '')
            .map((a) => ({ userId: a.userId, role: a.role }));

        // Validate: ensure exactly one lead (if there are assignments)
        if (assignmentsToSave.length > 0) {
            const leadCount = assignmentsToSave.filter((a) => a.role === 'lead').length;
            if (leadCount > 1) {
                // If multiple leads, keep only the first one as lead
                let foundFirstLead = false;
                assignmentsToSave.forEach((a) => {
                    if (a.role === 'lead') {
                        if (foundFirstLead) {
                            a.role = 'helper';
                        } else {
                            foundFirstLead = true;
                        }
                    }
                });
            }
        }

        onAssign(assignmentsToSave);
        setIsEditing(false);
        if (onEditingChange) {
            onEditingChange(false);
        }
    }, [selectedAssignments, canSave, onAssign, onEditingChange, setIsEditing, t]);

    const handleCancel = () => {
        setSelectedAssignments(mapAssignments(assignments));
        setIsEditing(false);
        if (onEditingChange) {
            onEditingChange(false);
        }
    };

    const isEditingMode = autoSave ? true : isEditing;

    // Compute available users (users not already assigned) - exclude all users in selectedAssignments AND assignments prop
    // This ensures already-assigned users don't appear in the dropdown
    // We use both selectedAssignments (current edit state) and assignments (saved state) to cover all cases
    const availableUsers = useMemo(() => {
        // Get all assigned user IDs from both sources
        // Use a Set to deduplicate and ensure O(1) lookup
        const assignedUserIds = new Set<string>();

        // Add IDs from selectedAssignments (current edit state - includes existing + newly added)
        selectedAssignments.forEach((a) => {
            if (a.userId && String(a.userId).trim()) {
                assignedUserIds.add(String(a.userId).trim());
            }
        });

        // Add IDs from assignments prop (saved state - existing assignments from backend)
        assignments.forEach((a) => {
            const assignmentUserId = String(getAssignmentUserId(a)).trim();
            if (assignmentUserId) {
                assignedUserIds.add(assignmentUserId);
            }
        });

        // Filter out any users whose ID is in the assigned set
        return users.filter((u) => {
            const userId = String(u.id).trim();
            return !assignedUserIds.has(userId);
        });
    }, [users, selectedAssignments, assignments]);

    /**
     * The unassigned people, grouped by what they do. Service workers lead the
     * list — they take the jobs — with the supervisors and managers who may also
     * be assigned below them.
     */
    const workerOptions = useMemo<SheetPickerOption[]>(() => {
        const groupRank: Record<string, number> = {
            'Service Worker': 0,
            'Service Supervisor': 1,
            'Service Manager': 2,
        };
        const groupLabels: Record<string, string> = {
            'Service Worker': t('work_orders.assign_worker.group_workers', 'Service workers'),
            'Service Supervisor': t('work_orders.assign_worker.group_supervisors', 'Service supervisors'),
            'Service Manager': t('work_orders.assign_worker.group_managers', 'Service managers'),
        };
        const otherLabel = t('work_orders.assign_worker.group_others', 'Other colleagues');

        return [...availableUsers]
            .sort((a, b) => {
                const rankA = groupRank[String(a.role ?? '')] ?? 3;
                const rankB = groupRank[String(b.role ?? '')] ?? 3;

                if (rankA !== rankB) {
                    return rankA - rankB;
                }

                return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
            })
            .map((user) => ({
                id: user.id,
                label: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
                description: user.position ?? user.email ?? undefined,
                keywords: [user.email, user.position, user.role].filter(Boolean).join(' '),
                badge: user.role ? (
                    <Badge variant="outline" className="text-xs">
                        {String(user.role)}
                    </Badge>
                ) : null,
                group: groupLabels[String(user.role ?? '')] ?? otherLabel,
            }));
    }, [availableUsers, t]);

    // Expose save function via ref
    useImperativeHandle(
        ref,
        () => ({
            save: handleSave,
        }),
        [handleSave],
    );

    const renderDisplay = () => {
        if (assignments.length === 0) {
            return <p className="text-muted-foreground text-sm italic">{t('work_orders.assign_worker.empty_assigned', 'No workers assigned')}</p>;
        }

        // Sort assignments: lead first, then helpers, maintaining original order within each group
        const sortedAssignments = [...assignments].sort((a, b) => {
            if (a.role === 'lead' && b.role !== 'lead') return -1;
            if (a.role !== 'lead' && b.role === 'lead') return 1;
            return 0;
        });

        return (
            <div className="space-y-3">
                {sortedAssignments.map((assignment) => {
                    const isLead = assignment.role === 'lead';
                    return (
                        <div key={assignment.id} className="bg-background flex items-center gap-3 rounded-lg border p-3">
                            <Avatar className="h-10 w-10">
                                <AvatarImage src={assignment.user?.avatar} />
                                <AvatarFallback>
                                    {assignment.user?.first_name?.[0]}
                                    {assignment.user?.last_name?.[0]}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <p className="font-medium">
                                        {assignment.user?.first_name} {assignment.user?.last_name}
                                    </p>
                                    {isLead && (
                                        <>
                                            <Crown className="text-warning-subtle-foreground h-4 w-4" />
                                            <Badge variant="outline" className="text-xs">
                                                {t('work_orders.assign_worker.lead', 'Lead')}
                                            </Badge>
                                        </>
                                    )}
                                    {!isLead && (
                                        <Badge variant="outline" className="text-xs">
                                            {t('work_orders.assign_worker.helper', 'Helper')}
                                        </Badge>
                                    )}
                                </div>
                                {assignment.user?.position && <p className="text-muted-foreground text-sm">{assignment.user.position}</p>}
                                {assignment.user?.email && <p className="text-muted-foreground text-xs">{assignment.user.email}</p>}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderEditor = () => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                {!noTitle && <label className="text-sm font-medium">{t('work_orders.assign_worker.label', 'Assign workers')}</label>}
                <Button type="button" variant="outline" size="sm" onClick={() => setIsAddDialogOpen(true)} className="h-8">
                    <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                    {t('work_orders.assign_worker.add', 'Add worker')}
                </Button>
            </div>

            {selectedAssignments.length === 0 ? (
                <p className="text-muted-foreground text-sm italic">{t('work_orders.assign_worker.empty_assigned', 'No workers assigned')}</p>
            ) : (
                <div className="space-y-3">
                    <RadioGroup
                        value={selectedAssignments.find((a) => a.role === 'lead')?.userId || ''}
                        onValueChange={handleSetLead}
                        className={!hasLead ? 'border-destructive rounded-lg border p-3' : ''}
                    >
                        {selectedAssignments.map((assignment) => {
                            const user = assignment.user || users.find((u) => u.id === assignment.userId);
                            return (
                                <div key={assignment.userId} className="bg-background flex items-center justify-between rounded-lg border p-3">
                                    <div className="flex flex-1 items-center gap-3">
                                        <RadioGroupItem value={assignment.userId} id={`lead-${assignment.userId}`} />
                                        <Avatar className="h-10 w-10">
                                            <AvatarImage src={user?.avatar} />
                                            <AvatarFallback>
                                                {user?.first_name?.[0]}
                                                {user?.last_name?.[0]}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium">
                                                    {user?.first_name} {user?.last_name}
                                                </p>
                                                {assignment.role === 'lead' ? (
                                                    <>
                                                        <Crown className="text-warning-subtle-foreground h-4 w-4" />
                                                        <Badge variant="outline" className="text-xs">
                                                            {t('work_orders.assign_worker.lead', 'Lead')}
                                                        </Badge>
                                                    </>
                                                ) : (
                                                    <Badge variant="outline" className="text-xs">
                                                        {t('work_orders.assign_worker.helper', 'Helper')}
                                                    </Badge>
                                                )}
                                            </div>
                                            {user?.position && <p className="text-muted-foreground text-sm">{user.position}</p>}
                                            {user?.email && <p className="text-muted-foreground text-xs">{user.email}</p>}
                                        </div>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleRemoveWorker(assignment.userId)}
                                        className="text-destructive hover:text-destructive h-8 w-8"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            );
                        })}
                    </RadioGroup>
                    {!hasLead && selectedAssignments.length > 0 && (
                        <p className="text-destructive text-sm">
                            {t('work_orders.assign_worker.lead_required', 'Please select a lead worker before saving')}
                        </p>
                    )}
                    {hasLead && (
                        <p className="text-muted-foreground text-xs">{t('work_orders.assign_worker.lead_hint', 'Select one worker as the lead')}</p>
                    )}
                </div>
            )}
            {!autoSave && (
                <div className="flex gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={handleCancel} className="flex-1">
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button type="button" onClick={handleSave} disabled={!canSave} className="flex-1">
                        <Check className="mr-2 h-4 w-4" />
                        {t('common.save', 'Save')}
                    </Button>
                </div>
            )}
        </div>
    );

    return (
        <div className="space-y-4">
            {!noTitle && (
                <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                        <h3 className="text-lg font-semibold">{t('work_orders.assign_worker.section_title', 'Assigned workers')}</h3>
                        <p className="text-muted-foreground text-sm">
                            {t('work_orders.assign_worker.section_subtitle', 'Set a lead worker and helpers')}
                        </p>
                    </div>
                    {!readOnly &&
                        !autoSave &&
                        (isEditing ? (
                            <div className="flex items-center gap-2">
                                <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
                                    {t('common.cancel', 'Cancel')}
                                </Button>
                                <Button type="button" size="sm" onClick={handleSave} disabled={!canSave}>
                                    <Check className="mr-2 h-4 w-4" />
                                    {t('common.save', 'Save')}
                                </Button>
                            </div>
                        ) : (
                            <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(true)} className="h-8">
                                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                                {assignments.length === 0 ? t('work_orders.assign_worker.label', 'Assign workers') : t('common.edit', 'Edit')}
                            </Button>
                        ))}
                </div>
            )}

            {isEditingMode && !readOnly ? renderEditor() : renderDisplay()}

            <ResponsiveModal
                open={isAddDialogOpen}
                onOpenChange={(open) => {
                    setIsAddDialogOpen(open);
                    if (!open) {
                        setNewUserId('');
                    }
                }}
                variant="sheet"
            >
                <ResponsiveModalContent size="md">
                    {/* Always rendered: Radix needs a title for the overlay to be
                        announced. `noTitle` hides it visually, it does not drop it. */}
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('work_orders.assign_worker.title', 'Add worker')}</ResponsiveModalTitle>
                        <ResponsiveModalDescription>
                            {t(
                                'work_orders.assign_worker.description',
                                'Pick who takes this job. Service workers first, then the supervisors and managers who can also be assigned.',
                            )}
                        </ResponsiveModalDescription>
                    </ResponsiveModalHeader>
                    <ResponsiveModalBody>
                        {/* Listed in the sheet rather than behind a combobox — a sheet
                            has the room, and a popover over it is a layer for nothing. */}
                        <SheetPickerList
                            value={newUserId || null}
                            onValueChange={setNewUserId}
                            options={workerOptions}
                            labels={{
                                search: t('work_orders.assign_worker.search', 'Search workers…'),
                                empty: t('work_orders.assign_worker.empty', 'No workers available.'),
                            }}
                        />
                    </ResponsiveModalBody>
                    <ResponsiveModalFooter className="gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setIsAddDialogOpen(false);
                                setNewUserId('');
                            }}
                        >
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button type="button" onClick={handleAddWorker} disabled={!newUserId}>
                            {t('common.add', 'Add')}
                        </Button>
                    </ResponsiveModalFooter>
                </ResponsiveModalContent>
            </ResponsiveModal>
        </div>
    );
});
